import { Cross2Icon } from "@radix-ui/react-icons";
import { Box, Button, Flex, IconButton, Text, Tooltip } from "@radix-ui/themes";
import { Control, Controller, FieldErrors, FieldValues, Path } from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import {
  ComposedName,
  FieldShell,
  fieldIds,
  resolveFieldError,
} from "../FieldShell";

interface SelectProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  control: Control<T>;
  errors: FieldErrors<T>;
  style?: any;
  fieldProps?: any;
  options?: { value: string; label: string }[];
  itemsLoading?: boolean;
  itemsError?: boolean;
  onSelect?: (value: string[]) => void;
  composedName?: ComposedName;
  required?: boolean;
  hint?: string;
  activateSelectAllButton?: boolean;
  setValue?: any;
}

/**
 * O react-select não conhece o tema Radix. Estas regras mapeiam-no para os
 * mesmos tokens usados pelos restantes campos, para que acompanhe o accent
 * color e o modo claro/escuro definidos no ThemeProvider.
 */
const radixStyles = (hasError: boolean): StylesConfig<any, true> => ({
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    minHeight: "var(--space-6)",
    backgroundColor: "var(--color-surface)",
    borderColor: hasError
      ? "var(--red-8)"
      : state.isFocused
      ? "var(--accent-8)"
      : "var(--gray-7)",
    borderRadius: "var(--radius-2)",
    boxShadow: state.isFocused
      ? `0 0 0 1px ${hasError ? "var(--red-8)" : "var(--accent-8)"}`
      : "none",
    "&:hover": { borderColor: hasError ? "var(--red-8)" : "var(--gray-8)" },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--color-panel-solid)",
    border: "1px solid var(--gray-6)",
    borderRadius: "var(--radius-3)",
    boxShadow: "var(--shadow-4)",
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--accent-9)"
      : state.isFocused
      ? "var(--accent-3)"
      : "transparent",
    color: state.isSelected ? "var(--accent-contrast)" : "var(--gray-12)",
    cursor: "pointer",
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "var(--accent-3)",
    borderRadius: "var(--radius-2)",
  }),
  multiValueLabel: (base) => ({ ...base, color: "var(--accent-11)" }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--accent-11)",
    ":hover": { backgroundColor: "var(--accent-5)", color: "var(--accent-12)" },
  }),
  placeholder: (base) => ({ ...base, color: "var(--gray-9)" }),
  input: (base) => ({ ...base, color: "var(--gray-12)" }),
  singleValue: (base) => ({ ...base, color: "var(--gray-12)" }),
  indicatorSeparator: (base) => ({ ...base, backgroundColor: "var(--gray-6)" }),
  dropdownIndicator: (base) => ({ ...base, color: "var(--gray-9)" }),
  noOptionsMessage: (base) => ({ ...base, color: "var(--gray-10)" }),
});

const MultiSelectComponent = <T extends FieldValues>({
  name,
  label,
  control,
  errors,
  style,
  fieldProps,
  options,
  onSelect,
  composedName,
  itemsError,
  itemsLoading,
  required,
  hint,
  activateSelectAllButton,
  setValue,
}: SelectProps<T>) => {
  const error = resolveFieldError(errors, name, composedName);
  const { fieldId } = fieldIds(name);

  const handleOnClickSelectAll = (event: any) => {
    event.preventDefault();
    if (options && options.length > 0) {
      const allValues = options.map((option) => option.value);
      if (setValue) {
        setValue(name, allValues as any, { shouldValidate: true });
        if (onSelect) onSelect(allValues);
      }
    }
  };

  return (
    <Box {...style}>
      <FieldShell name={name} label={label} required={required} error={error} hint={hint}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const hasSelection = field.value?.length > 0;
            const canClear = hasSelection && !fieldProps?.disabled;

            return (
              <Flex direction="column" gap="2">
                <Select
                  inputId={fieldId}
                  aria-label={label}
                  isMulti
                  options={options}
                  isLoading={itemsLoading}
                  isDisabled={itemsError || fieldProps?.disabled}
                  value={options?.filter((opt) => field.value?.includes(opt.value)) || []}
                  onChange={(selectedOptions: any) => {
                    const values = selectedOptions.map((option: any) => option.value);
                    field.onChange(values);
                    if (onSelect) onSelect(values);
                  }}
                  classNamePrefix="react-select"
                  {...fieldProps}
                  placeholder="Selecionar…"
                  noOptionsMessage={() => "Sem opções disponíveis"}
                  loadingMessage={() => "A carregar…"}
                  menuPortalTarget={document.body}
                  styles={radixStyles(!!error)}
                />

                <Flex gap="2" align="center">
                  {canClear && (
                    <Tooltip content="Limpar selecção">
                      <IconButton
                        type="button"
                        variant="ghost"
                        color="gray"
                        size="1"
                        aria-label={`Limpar ${label}`}
                        onClick={() => {
                          field.onChange([]);
                          if (onSelect) onSelect([]);
                        }}
                      >
                        <Cross2Icon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!canClear && activateSelectAllButton && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="1"
                      onClick={handleOnClickSelectAll}
                    >
                      Selecionar todos
                    </Button>
                  )}
                  {hasSelection && (
                    <Text size="1" color="gray">
                      {field.value.length} seleccionado{field.value.length > 1 ? "s" : ""}
                    </Text>
                  )}
                </Flex>
              </Flex>
            );
          }}
        />
      </FieldShell>
    </Box>
  );
};

export default MultiSelectComponent;
